// Citation generation utilities for APA 7th edition format

import { resolveConferenceUnifiedSubcategory } from './citationFieldMapper.js';
import { finalizeReferenceHtml } from './referenceOutputUtils.js';

function polishReferenceOutput(result) {
	if (!result || result.startsWith('Fill in')) return result || '';
	if (/personal communications are cited only/i.test(result)) return result;
	return finalizeReferenceHtml(result);
}

/**
 * Convert text to sentence case
 * - Capitalizes first letter
 * - Lowercases the rest
 * - Used for Book Titles per APA rules (user acknowledged proper noun limitation)
 * @param {string} text - Text to convert
 * @returns {string} - Sentence cased text
 */
export const toSentenceCase = (text) => {
	if (!text) return '';

	const trimmed = text.trim();
	if (!trimmed) return '';

	// If the user already used mixed capitalization, keep it (assume they
	// already applied proper nouns/acronyms manually).
	const isAllUpper = trimmed === trimmed.toUpperCase() && /[A-Z]/.test(trimmed);
	const isAllLower = trimmed === trimmed.toLowerCase() && /[a-z]/.test(trimmed);

	if (!isAllUpper && !isAllLower) {
		return trimmed;
	}

	// Preserve acronyms/initialisms that are fully uppercase in the input.
	// Example: APA, NASA, etc.
	const acronymMatches = trimmed.match(/\b[A-Z0-9]{2,}\b/g) || [];
	const acronyms = new Set(acronymMatches.map((w) => w.toUpperCase()));

	// Normalize to lowercase first, then apply APA-ish sentence capitalization:
	// - capitalize the first word
	// - capitalize the word after sentence-ending punctuation and colons
	const lower = trimmed.toLowerCase();
	const capitalized = lower.replace(/(^|[.!?:]\s*)([a-z])/g, (_m, prefix, letter) => {
		return `${prefix}${letter.toUpperCase()}`;
	});

	// Restore acronyms (replace whole words case-insensitively).
	let restored = capitalized;
	for (const acronym of acronyms) {
		const lowerAcr = acronym.toLowerCase();
		restored = restored.replace(new RegExp(`\\b${lowerAcr}\\b`, 'g'), acronym);
	}

	return restored;
};

/**
 * Helper to normalize capitalization
 * - Converts ALL CAPS to Title Case
 * - Converts all lowercase to Title Case
 * - Preserves mixed case (e.g., "McCarthy", "O'Connor")
 * @param {string} text - Text to normalize
 * @returns {string} - Normalized text
 */
export const normalizeCapitalization = (text) => {
	if (!text) return '';

	// Check if text is all upper or all lower
	const isAllUpper = text === text.toUpperCase();
	const isAllLower = text === text.toLowerCase();

	if (isAllUpper || isAllLower) {
		// Convert to Title Case (handles hyphens and spaces)
		return text.replace(
			/\w\S*/g,
			(txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
		);
	}

	return text;
};

/** Format publisher/platform names — title-case ALL CAPS input, preserve mixed case. */
function formatPublisherName(raw) {
	if (!raw || !String(raw).trim()) return '';
	return normalizeCapitalization(String(raw).trim()).replace(/\.\s*$/, '');
}

/** Format a list that may contain personal names or organization names. */
export function formatMixedNameListForReference(names) {
	const valid = (names || []).filter((n) => n && String(n).trim());
	if (valid.length === 0) return '';
	const formatted = valid.map((n) =>
		looksLikeOrganizationName(n) ? String(n).trim() : formatNameForReference(n)
	);
	if (formatted.length === 1) return formatted[0];
	if (formatted.length === 2) return `${formatted[0]}, & ${formatted[1]}`;
	const allButLast = formatted.slice(0, -1).join(', ');
	return `${allButLast}, & ${formatted[formatted.length - 1]}`;
}

/** Format a list for in-text citations (org names preserved). */
function formatMixedNameListForInText(names) {
	const valid = (names || []).filter((n) => n && String(n).trim());
	if (valid.length === 0) return [];
	return valid.map((n) =>
		looksLikeOrganizationName(n) ? String(n).trim() : formatNameForInText(n)
	);
}

/**
 * Parse a full name into components (first, middle, last)
 * Assumes format: "FirstName MiddleName LastName" or "FirstName LastName"
 * @param {string} fullName - Full name string (e.g., "Abdul Had Ali")
 * @returns {object} - Object with firstName, middleName, lastName
 */
export const parseName = (fullName) => {
	if (!fullName || !fullName.trim()) {
		return { firstName: '', middleName: '', lastName: '' };
	}

	const nameParts = fullName
		.trim()
		.split(/\s+/)
		.filter((part) => part.length > 0)
		.map(normalizeCapitalization); // Normalize each part

	if (nameParts.length === 0) {
		return { firstName: '', middleName: '', lastName: '' };
	} else if (nameParts.length === 1) {
		return { firstName: nameParts[0], middleName: '', lastName: '' };
	} else if (nameParts.length === 2) {
		return { firstName: nameParts[0], middleName: '', lastName: nameParts[1] };
	} else {
		// Three or more parts: first is firstName, last is lastName, everything in between is middleName
		const firstName = nameParts[0];
		const lastName = nameParts[nameParts.length - 1];
		const middleName = nameParts.slice(1, -1).join(' ');
		return { firstName, middleName, lastName };
	}
};

/**
 * Format a single name for reference citation
 * Format: "LastName, F. M." (lastname, firstname initial, middle name initial)
 * @param {string} fullName - Full name string
 * @returns {string} - Formatted name
 */
export const formatNameForReference = (fullName) => {
	// Check if already formatted as "LastName, I." or "LastName, I. M."
	// Regex: Starts with chars, comma, space, then one or more "Initial." groups (case-insensitive)
	const preFormattedMatch = fullName.trim().match(/^([^,]+),\s+((?:[a-zA-Z]\.\s*)+)$/);
	if (preFormattedMatch) {
		const lastName = normalizeCapitalization(preFormattedMatch[1]);
		const initials = preFormattedMatch[2].toUpperCase(); // Ensure initials are uppercase
		return `${lastName}, ${initials}`;
	}

	const { firstName, middleName, lastName } = parseName(fullName);

	if (!lastName && !firstName) return '';

	if (!lastName) return firstName; // If only first name exists

	let formatted = lastName;

	if (firstName) {
		formatted += `, ${firstName.charAt(0).toUpperCase()}.`;
	}

	if (middleName) {
		// Handle multiple middle names
		const middleInitials = middleName
			.split(/\s+/)
			.map((name) => name.charAt(0).toUpperCase() + '.')
			.join(' ');
		formatted += ` ${middleInitials}`;
	}

	return formatted;
};

/**
 * Format a single translator name for reference citation
 * Format: "F. M. LastName" (firstname initial, middle name initial, lastname full)
 * Example: "Abdul Bais Khan" → "A. B. Khan"
 * @param {string} fullName - Full name string
 * @returns {string} - Formatted translator name
 */
export const formatTranslatorForReference = (fullName) => {
	const { firstName, middleName, lastName } = parseName(fullName);

	if (!lastName && !firstName) return '';

	if (!lastName) return firstName; // If only first name exists

	// Check if already formatted as "I. LastName" or "I. M. LastName"
	// Regex: Starts with one or more "Initial." groups, then space, then LastName (single word, no spaces)
	// This prevents "A. MiddleName LastName" from being captured as "A. " + "MiddleName LastName"
	const preFormattedMatch = fullName.trim().match(/^((?:[a-zA-Z]\.\s*)+)([^.\s]+)$/);
	if (preFormattedMatch) {
		const initials = preFormattedMatch[1].toUpperCase(); // Ensure initials are uppercase
		const lastName = normalizeCapitalization(preFormattedMatch[2].trim());
		return `${initials} ${lastName}`;
	}

	let formatted = '';

	// Add first name initial
	if (firstName) {
		formatted = `${firstName.charAt(0).toUpperCase()}.`;
	}

	// Add middle name initials
	if (middleName) {
		const middleInitials = middleName
			.split(/\s+/)
			.map((name) => name.charAt(0).toUpperCase() + '.')
			.join(' ');
		formatted += ` ${middleInitials}`;
	}

	// Add last name (full)
	if (formatted) {
		formatted += ` ${lastName}`;
	} else {
		formatted = lastName;
	}

	return formatted;
};

/**
 * Format a single name for in-text citation
 * Format: "LastName" (lastname only)
 * @param {string} fullName - Full name string
 * @returns {string} - Just the last name
 */
export const formatNameForInText = (fullName) => {
	// Check if already formatted as "LastName, I." (Reference format)
	const preFormattedMatch = fullName.trim().match(/^([^,]+),\s+(?:[a-zA-Z]\.\s*)+$/);
	if (preFormattedMatch) {
		// Extract just the part before the comma and normalize
		return normalizeCapitalization(preFormattedMatch[1]);
	}

	const { lastName, firstName } = parseName(fullName);
	return lastName || firstName || 'Author';
};

/**
 * Parse organization name to extract full name and abbreviation
 * Detects format: "Full Organization Name (ABBR)"
 * @param {string} orgName - Organization name string
 * @returns {object} - Object with fullName and abbreviation
 * @example parseOrganizationName("World Health Organization (WHO)")
 *          returns { fullName: "World Health Organization", abbreviation: "WHO", hasAbbreviation: true }
 */
export const parseOrganizationName = (orgName) => {
	if (!orgName || !orgName.trim()) {
		return { fullName: '', abbreviation: '', hasAbbreviation: false };
	}

	// Match pattern: "Full Name (ABBR)" where ABBR is in parentheses at the end
	const match = orgName.match(/^(.+?)\s*\(([^)]+)\)\s*$/);

	if (match) {
		const fullName = match[1].trim();
		const abbreviation = match[2].trim();
		return {
			fullName,
			abbreviation,
			hasAbbreviation: true
		};
	}

	// No abbreviation found, return the whole name
	return {
		fullName: orgName.trim(),
		abbreviation: '',
		hasAbbreviation: false
	};
};

/**
 * Resolve organization display name and optional abbreviation (APA 7 group author).
 * Uses optional `groupAuthorAbbreviation` field, else trailing "(ABBR)" in organization string.
 */
export const getOrganizationCitationParts = (data) => {
	const abbrRaw = (data.groupAuthorAbbreviation || '').trim();
	const orgRaw = (data.organization || '').trim();
	if (!orgRaw) {
		return { fullName: '', abbreviation: '', hasAbbreviation: false };
	}
	if (abbrRaw) {
		return { fullName: orgRaw, abbreviation: abbrRaw.toUpperCase(), hasAbbreviation: true };
	}
	const parsed = parseOrganizationName(orgRaw);
	return {
		fullName: parsed.fullName,
		abbreviation: parsed.abbreviation,
		hasAbbreviation: parsed.hasAbbreviation
	};
};

/**
 * APA 7 in-text pair for group authors (parenthetical uses [ABBR] on first cite).
 */
export const formatOrganizationIntextPair = (data, yearText, isFirstUse = true) => {
	const { fullName, abbreviation, hasAbbreviation } = getOrganizationCitationParts(data);
	if (!fullName) {
		return {
			parenthetical: `(Organization, ${yearText})`,
			narrative: `Organization (${yearText})`
		};
	}
	if (!hasAbbreviation) {
		return {
			parenthetical: `(${fullName}, ${yearText})`,
			narrative: `${fullName} (${yearText})`
		};
	}
	if (isFirstUse) {
		return {
			parenthetical: `(${fullName} [${abbreviation}], ${yearText})`,
			narrative: `${fullName} (${abbreviation}, ${yearText})`
		};
	}
	return {
		parenthetical: `(${abbreviation}, ${yearText})`,
		narrative: `${abbreviation} (${yearText})`
	};
};

/** Single-string org snippet (legacy); in-text assembly should use formatOrganizationIntextPair. */
export const formatOrganizationForInText = (orgName, isFirstUse = true) => {
	const { fullName, abbreviation, hasAbbreviation } = parseOrganizationName(orgName || '');
	if (!hasAbbreviation) return fullName || 'Organization';
	return isFirstUse ? `${fullName} (${abbreviation})` : abbreviation;
};

function normalizeApaPublisherComparable(s) {
	if (!s || typeof s !== 'string') return '';
	return s.replace(/\s+/g, ' ').trim().replace(/\.+$/, '').toLowerCase();
}

/** APA 7: omit publisher when it duplicates the author element (after normalization). */
function shouldOmitPublisherWhenMatchesAuthor(data, authorFormattedForReference, publisherRaw) {
	const pub = normalizeApaPublisherComparable(publisherRaw);
	if (!pub) return false;
	if (data.AuthorOrOrganization === 'Organization' && data.organization) {
		const { fullName } = getOrganizationCitationParts(data);
		const orgA = normalizeApaPublisherComparable(fullName);
		const orgB = normalizeApaPublisherComparable(data.organization);
		return pub === orgA || pub === orgB;
	}
	const auth = normalizeApaPublisherComparable(authorFormattedForReference);
	if (auth.length > 0 && auth === pub) return true;

	// Also treat identical raw author ↔ publisher strings as a match (e.g. both "Stephen King")
	const authors = (data.authors || data.author || []).filter((a) => a && String(a).trim());
	if (authors.some((a) => normalizeApaPublisherComparable(a) === pub)) return true;
	for (const a of authors) {
		const one = formatAuthorsForReference([a]);
		if (one && normalizeApaPublisherComparable(one) === pub) return true;
	}
	return false;
}

/**
 * Format multiple authors for reference citation
 * Rules:
 * - 1 author: "LastName, F. M."
 * - 2 authors: "LastName1, F. M., & LastName2, F. M."
 * - 3+ authors: "LastName1, F. M., LastName2, F. M., & LastName3, F. M."
 * @param {Array} authors - Array of full names
 * @returns {string} - Formatted authors string
 */
export const formatAuthorsForReference = (authors) => {
	if (!authors || authors.length === 0 || !authors[0]) return '';

	const validAuthors = authors.filter((a) => a && a.trim());
	if (validAuthors.length === 0) return '';

	const formattedAuthors = validAuthors.map((author) => formatNameForReference(author));

	if (formattedAuthors.length === 1) {
		return formattedAuthors[0];
	} else if (formattedAuthors.length === 2) {
		return `${formattedAuthors[0]}, & ${formattedAuthors[1]}`;
	} else {
		// For 3 or more authors (up to 20 per requirement), list ALL authors
		// Format: "Author1, F. M., Author2, F. M., & Author3, F. M."
		const allButLast = formattedAuthors.slice(0, -1).join(', ');
		const last = formattedAuthors[formattedAuthors.length - 1];
		return `${allButLast}, & ${last}`;
	}
};

/**
 * Format multiple authors for in-text citation
 * Rules:
 * - 1 author: "LastName"
 * - 2 authors: "LastName1 and LastName2" (narrative) or "LastName1 & LastName2" (parenthetical)
 * - 3+ authors: "LastName1 et al."
 * @param {Array} authors - Array of full names
 * @param {boolean} isNarrative - True for narrative citations (uses "and"), false for parenthetical (uses "&")
 * @returns {string} - Formatted authors string for in-text
 */
export const formatAuthorsForInText = (authors, isNarrative = false) => {
	if (!authors || authors.length === 0 || !authors[0]) return 'Author';

	const validAuthors = authors.filter((a) => a && a.trim());
	if (validAuthors.length === 0) return 'Author';

	if (validAuthors.length === 1) {
		return formatNameForInText(validAuthors[0]);
	} else if (validAuthors.length === 2) {
		// Use "and" for narrative citations, "&" for parenthetical
		const conjunction = isNarrative ? 'and' : '&';
		return `${formatNameForInText(validAuthors[0])} ${conjunction} ${formatNameForInText(validAuthors[1])}`;
	} else {
		return `${formatNameForInText(validAuthors[0])} et al.`;
	}
};


/**
 * Format multiple translators for reference citation
 * Rules:
 * - 1 translator: "F. M. LastName"
 * - 2 translators: "F. M. LastName1 & F. M. LastName2"
 * - 3+ translators: "F. M. LastName1 et al."
 * @param {Array} translators - Array of full names
 * @returns {string} - Formatted translators string
 */
export const formatTranslatorsForReference = (translators) => {
	if (!translators || translators.length === 0 || !translators[0]) return '';

	const validTranslators = translators.filter((t) => t && t.trim());
	if (validTranslators.length === 0) return '';

	const formattedTranslators = validTranslators.map((translator) =>
		formatTranslatorForReference(translator)
	);

	if (formattedTranslators.length === 1) {
		return formattedTranslators[0];
	} else if (formattedTranslators.length === 2) {
		return `${formattedTranslators[0]} & ${formattedTranslators[1]}`;
	} else {
		// Parenthetical contributors (e.g., translators): no Oxford comma before "&"
		const allButLast = formattedTranslators.slice(0, -1).join(', ');
		return `${allButLast} & ${formattedTranslators[formattedTranslators.length - 1]}`;
	}
};

/**
 * Format editors for "In M. Joe (Ed.)" / "In M. Joe, F. Fee, & H. Hee (Eds.)" phrases.
 * Uses initials-first names; 3+ editors include Oxford comma before "&".
 */
export const formatEditorsForReference = (editors) => {
	if (!editors || editors.length === 0 || !editors[0]) return '';

	const validEditors = editors.filter((e) => e && e.trim());
	if (validEditors.length === 0) return '';

	const formattedEditors = validEditors.map((editor) => formatTranslatorForReference(editor));

	if (formattedEditors.length === 1) {
		return formattedEditors[0];
	}
	if (formattedEditors.length === 2) {
		return `${formattedEditors[0]} & ${formattedEditors[1]}`;
	}
	const allButLast = formattedEditors.slice(0, -1).join(', ');
	return `${allButLast}, & ${formattedEditors[formattedEditors.length - 1]}`;
};

function getTranslatorList(data) {
	const raw = data.translators || data.translator;
	if (!raw) return [];
	return Array.isArray(raw) ? raw : [raw];
}

/** Prefer source-specific author lists over a stale generic `author` field (edit-mode sync). */
function resolveInTextAuthorList(category, sourceType, data) {
	if (category === 'Book & Chapter') {
		if (sourceType === 'Chapter in Edited Book') {
			const chapterAuthors = (data.chapterAuthors || []).filter((a) => a && a.trim());
			if (chapterAuthors.length) return chapterAuthors;
		}
		if (sourceType === 'Reference Work Entry') {
			const entryAuthors = (data.entryAuthors || []).filter((a) => a && a.trim());
			if (entryAuthors.length) return entryAuthors;
		}
		if (sourceType === 'Book') {
			const bookAuthors = (data.authors || data.author || []).filter((a) => a && a.trim());
			if (bookAuthors.length) return bookAuthors;
		}
	}
	if (category === 'Multimedia') {
		if (
			(sourceType === 'Webinar/Lecture (recorded & retrievable)' ||
				sourceType === 'Webinar/Lecture') &&
			data.presentersLecturers?.some((a) => a && a.trim())
		) {
			return data.presentersLecturers.filter((a) => a && a.trim());
		}
		if (sourceType === 'Conference Presentation' && data.presentersOrganization?.some((a) => a && a.trim())) {
			return data.presentersOrganization.filter((a) => a && a.trim());
		}
		if (sourceType === 'Conference Session' && data.contributorsOrganization?.some((a) => a && a.trim())) {
			return data.contributorsOrganization.filter((a) => a && a.trim());
		}
		if (
			(sourceType === 'Conference Presentation / Session' ||
				sourceType === 'Conference Presentation') &&
			data.contributorsOrganization?.some((a) => a && a.trim())
		) {
			return data.contributorsOrganization.filter((a) => a && a.trim());
		}
		if (sourceType === 'Radio Broadcast' && data.contributorsOrganization?.some((a) => a && a.trim())) {
			return data.contributorsOrganization.filter((a) => a && a.trim());
		}
		if (sourceType === 'Film/Movie' && data.contributors?.some((a) => a && a.trim())) {
			return data.contributors.filter((a) => a && a.trim());
		}
		if (sourceType === 'Song/Track' && data.artistGroup?.some((a) => a && a.trim())) {
			return data.artistGroup.filter((a) => a && a.trim());
		}
	}
	if (category === 'Course & Education' && sourceType === 'Course Handout') {
		const presenters = (data.presenterOrganization || []).filter((a) => a && a.trim());
		if (presenters.length) return presenters;
	}
	if (data.author?.some((a) => a && a.trim())) {
		return data.author.filter((a) => a && a.trim());
	}
	const fallback =
		data.authors ||
		data.chapterAuthors ||
		data.entryAuthors ||
		data.presentersOrganization ||
		data.presenterOrganization ||
		data.authorUploader ||
		data.writers ||
		data.contributors ||
		data.presentersLecturers ||
		data.contributorsOrganization ||
		data.artistGroup ||
		[];
	return fallback.filter((a) => a && a.trim());
}

/**
 * Format multiple contributors for reference citation
 * Rules:
 * - 1 contributor: "LastName, F. M."
 * - 2 contributors: "LastName1, F. M., & LastName2, F. M."
 * - 3+ contributors: "LastName1, F. M., LastName2, F. M., & LastName3, F. M." (ALL listed, no et al.)
 * @param {Array} contributors - Array of full names
 * @returns {string} - Formatted contributors string (all contributors listed)
 */
export const formatContributorsForReference = (contributors) => {
	if (!contributors || contributors.length === 0 || !contributors[0]) return '';

	const validContributors = contributors.filter((c) => c && c.trim());
	if (validContributors.length === 0) return '';

	const formattedContributors = validContributors.map((contributor) =>
		formatNameForReference(contributor)
	);

	if (formattedContributors.length === 1) {
		return formattedContributors[0];
	} else if (formattedContributors.length === 2) {
		return `${formattedContributors[0]}, & ${formattedContributors[1]}`;
	} else {
		// For 3+ contributors, list ALL with commas and & before last one
		const allButLast = formattedContributors.slice(0, -1).join(', ');
		return `${allButLast}, & ${formattedContributors[formattedContributors.length - 1]}`;
	}
};

// Legacy helper - kept for backward compatibility, now uses new functions
export const formatAuthors = (authors) => {
	return formatAuthorsForReference(authors);
};

// Legacy helper - kept for backward compatibility
export const getFirstAuthorLastName = (authors) => {
	if (!authors || authors.length === 0 || !authors[0]) return 'Author';

	const validAuthors = authors.filter((a) => a && a.trim());
	if (validAuthors.length === 0) return 'Author';

	return formatNameForInText(validAuthors[0]);
};

// Helper to format year for in-text citation
export const extractYear = (dateOrYear) => {
	if (!dateOrYear) return 'n.d.';

	const trimmed = dateOrYear.trim();
	const compact = trimmed.replace(/\s+/g, '');

	// Reject invalid placeholders
	if (
		!trimmed ||
		/^unknown$/i.test(trimmed) ||
		/^n\/a$/i.test(trimmed) ||
		/^\(\)$/.test(trimmed)
	) {
		return 'n.d.';
	}

	// "nd", "n.d", "n.d." → n.d. for in-text
	if (/^n\.?d\.?$/i.test(compact) || /^nd$/i.test(trimmed)) {
		return 'n.d.';
	}

	// If it's just a year
	if (/^\d{4}$/.test(trimmed)) {
		return trimmed;
	}

	// If it's a full date, extract the year (last 4 digits)
	const yearMatch = trimmed.match(/\d{4}/);
	return yearMatch ? yearMatch[0] : 'n.d.';
};

const MONTHS_LONG = [
	'January',
	'February',
	'March',
	'April',
	'May',
	'June',
	'July',
	'August',
	'September',
	'October',
	'November',
	'December'
];

/**
 * Build the date portion inside reference-list parentheses: (2020), (2020, January), (2020, May 16), (2019, Spring), (n.d.).
 */
export const formatAPAReferenceParentheticalDate = (dateOrYear) => {
	if (!dateOrYear || !String(dateOrYear).trim()) return 'n.d.';
	const s = String(dateOrYear).trim();
	const compact = s.replace(/\s+/g, '');
	if (/^unknown$/i.test(s) || /^n\/a$/i.test(s)) return 'n.d.';
	if (/^n\.?d\.?$/i.test(compact) || /^nd$/i.test(s)) return 'n.d.';
	if (/^\d{4}$/.test(s)) return s;
	const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
	if (iso) {
		const mi = parseInt(iso[2], 10) - 1;
		const day = parseInt(iso[3], 10);
		if (mi >= 0 && mi < 12) {
			return `${iso[1]}, ${MONTHS_LONG[mi]} ${day}`;
		}
	}
	if (/^\d{4},\s/.test(s)) return s;
	const season = s.match(/^(\d{4}),\s*(Spring|Summer|Fall|Autumn|Winter)$/i);
	if (season) {
		const cap = season[2].charAt(0).toUpperCase() + season[2].slice(1).toLowerCase();
		return `${season[1]}, ${cap}`;
	}
	const yearMatch = s.match(/\d{4}/);
	return yearMatch ? yearMatch[0] : 'n.d.';
};

/**
 * Format a stored date value for "Retrieved Month D, YYYY, from …" (reference list).
 */
export const formatRetrievalDatePhrase = (dateOrYear) => {
	if (!dateOrYear || !String(dateOrYear).trim()) return '';
	const s = String(dateOrYear).trim();
	const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
	if (iso) {
		const mi = parseInt(iso[2], 10) - 1;
		const day = parseInt(iso[3], 10);
		if (mi >= 0 && mi < 12) {
			return `${MONTHS_LONG[mi]} ${day}, ${iso[1]}`;
		}
	}
	if (/^\d{4}-\d{2}-\d{2}T/.test(s)) {
		const d = new Date(s);
		if (!Number.isNaN(d.getTime())) {
			return `${MONTHS_LONG[d.getUTCMonth()]} ${d.getUTCDate()}, ${d.getUTCFullYear()}`;
		}
	}
	// "2024, January 15" or "2024, Jan 15"
	const yearFirst = s.match(/^(\d{4}),\s*([A-Za-z]+)\s+(\d{1,2})\b/);
	if (yearFirst) {
		return `${yearFirst[2]} ${parseInt(yearFirst[3], 10)}, ${yearFirst[1]}`;
	}
	// "January 15, 2024"
	const monthFirst = s.match(/^([A-Za-z]+)\s+(\d{1,2}),\s*(\d{4})\b/);
	if (monthFirst) {
		return `${monthFirst[1]} ${parseInt(monthFirst[2], 10)}, ${monthFirst[3]}`;
	}
	return s;
};

function formatDatasetVersionForReference(versionRaw) {
	if (!versionRaw || !String(versionRaw).trim()) return '';
	const v = String(versionRaw).trim().replace(/^v\.?\s*/i, '').trim();
	if (!v) return '';
	return ` (Version ${v})`;
}

/**
 * In-text name for personal communication: "B. Hill" from "Bridgette Hill" or "Hill, B."
 */
export function formatPersonalCommunicationNameForInText(fullName) {
	if (!fullName || !String(fullName).trim()) return 'Communicator';
	const raw = fullName.trim();
	const ref = formatNameForReference(raw);
	const commaForm = ref.match(/^([^,]+),\s*((?:[A-Za-z]\.\s*)+)/);
	if (commaForm) {
		const last = commaForm[1].trim();
		const initials = commaForm[2].trim().replace(/\s+/g, ' ');
		return `${initials} ${last}`.replace(/\s+/g, ' ').trim();
	}
	const { firstName, middleName, lastName } = parseName(raw);
	if (!lastName) return raw;
	let initials = '';
	if (firstName) initials += `${firstName.charAt(0).toUpperCase()}.`;
	if (middleName) {
		for (const m of middleName.split(/\s+/)) {
			if (m) initials += ` ${m.charAt(0).toUpperCase()}.`;
		}
	}
	return initials ? `${initials.trim()} ${lastName}` : lastName;
}

// Helper to format page range (replace hyphens with en-dashes)
export const formatPageRange = (range) => {
	if (!range) return '';
	return range.replace(/-/g, '–');
};

export function truncateToWordCount(text, maxWords = 20) {
	if (!text) return '';
	const words = String(text).trim().split(/\s+/).filter(Boolean);
	if (words.length <= maxWords) return words.join(' ');
	return words.slice(0, maxWords).join(' ');
}

export function looksLikeOrganizationName(name, role = '') {
	const raw = String(name || '').trim();
	if (!raw) return false;
	if (/organization\s*\(no role/i.test(role)) return true;
	if (/^[^,]+,\s*[A-Za-z](?:\.|\s|$)/.test(raw)) return false;
	if (
		/\b(Inc\.?|LLC|Ltd\.?|Corp\.?|Co\.?|Bros\.?|Radio|University|Association|Institute|Organization|BBC|NPR|WHO|CDC|Airlines|Media|Studios?)\b/i.test(
			raw
		)
	) {
		return true;
	}
	if (raw.split(/\s+/).length >= 2 && !raw.includes(',')) return true;
	return false;
}

function formatContributorNameForReference(name, role = '') {
	if (!name?.trim()) return '';
	if (looksLikeOrganizationName(name, role)) return name.trim();
	return formatNameForReference(name);
}

function joinReferenceContributorSegments(segments) {
	const valid = (segments || []).filter((s) => s && String(s).trim());
	if (valid.length === 0) return '';
	if (valid.length === 1) return valid[0];
	if (valid.length === 2) return `${valid[0]}, & ${valid[1]}`;
	return `${valid.slice(0, -1).join(', ')}, & ${valid[valid.length - 1]}`;
}

function formatContributorsWithRolesForReference(contributorsList, roles = [], options = {}) {
	const { orgSeparator = ', ' } = options;
	const validContributors = (contributorsList || []).filter((c) => c && c.trim());
	if (validContributors.length === 0) return '';

	const formattedNames = validContributors.map((contributor, index) => {
		const role = (roles[index] || '').trim();
		const name = formatContributorNameForReference(contributor, role);
		if (!role || /organization\s*\(no role/i.test(role)) return name;
		return `${name} (${role})`;
	});

	const allOrgs = validContributors.every((c, i) =>
		looksLikeOrganizationName(c, roles[i] || '')
	);
	if (allOrgs && formattedNames.length > 1) {
		return formattedNames.join(orgSeparator === '; ' ? '; ' : orgSeparator);
	}

	return joinReferenceContributorSegments(formattedNames);
}

// Helper to format edition number to ordinal (e.g. "5" -> "5th")
export const formatEdition = (edition) => {
	if (!edition) return '';

	// If it's just a number, add ordinal suffix
	if (/^\d+$/.test(edition.trim())) {
		const num = parseInt(edition.trim(), 10);
		const j = num % 10;
		const k = num % 100;

		if (j === 1 && k !== 11) {
			return num + "st";
		}
		if (j === 2 && k !== 12) {
			return num + "nd";
		}
		if (j === 3 && k !== 13) {
			return num + "rd";
		}
		return num + "th";
	}

	// Otherwise return as is (e.g., "Revised", "3rd")
	return edition;
};

/**
 * APA 7 parenthetical after a book title: (Translator, Trans.; ed., Vol., pages)
 * Use a semicolon after "Trans." when edition and/or volume (or other trailing info) follows.
 * @param {{ translatorText?: string, trailingParts?: string[] }} opts
 * @returns {string} e.g. " (M. Garcia, Trans.; 2nd ed., Vol. 10)" or ""
 */
export function formatTranslatorEditionVolumeParenthetical({
	translatorText = '',
	trailingParts = [],
} = {}) {
	const trans = (translatorText || '').trim();
	const trailing = (trailingParts || []).map((p) => String(p || '').trim()).filter(Boolean);

	if (trans && trailing.length) {
		return ` (${trans}, Trans.; ${trailing.join(', ')})`;
	}
	if (trans) {
		return ` (${trans}, Trans.)`;
	}
	if (trailing.length) {
		return ` (${trailing.join(', ')})`;
	}
	return '';
}

function formatVolumeLabel(volumes) {
	if (!volumes) return '';
	const volumeLabel =
		String(volumes).includes('-') || String(volumes).includes(',') ? 'Vols.' : 'Vol.';
	return `${volumeLabel} ${volumes}`;
}

// Generate reference citation for different source types
export const generateReference = (category, sourceType, data) => {
	if (!category || !sourceType || !data) return '';

	try {
		// Web Content
		if (category === 'Web Content') {
			if (sourceType === 'Blog Post') {
				return polishReferenceOutput(generateBlogPost(data));
			} else if (sourceType === 'Webpage') {
				return polishReferenceOutput(generateWebpage(data));
			}
		}

		// Periodicals
		if (category === 'Periodicals') {
			if (sourceType === 'Journal Article') {
				return polishReferenceOutput(generateJournalArticle(data));
			} else if (sourceType === 'Magazine Article (Print & Online)') {
				return polishReferenceOutput(generateMagazineArticle(data));
			} else if (sourceType === 'Newspaper Article (Print & Online)') {
				return polishReferenceOutput(generateNewspaperArticle(data));
			} else if (sourceType === 'Preprint') {
				return polishReferenceOutput(generatePreprint(data));
			}
		}

		// Book & Chapter
		if (category === 'Book & Chapter') {
			if (sourceType === 'Book') {
				return polishReferenceOutput(generateBook(data));
			} else if (sourceType === 'Chapter in Edited Book') {
				return polishReferenceOutput(generateBookChapter(data));
			} else if (sourceType === 'Reference Work Entry') {
				return polishReferenceOutput(generateReferenceEntry(data));
			} else if (sourceType === 'Thesis/Dissertation') {
				return polishReferenceOutput(generateThesis(data));
			}
		}

		// Multimedia
		if (category === 'Multimedia') {
			if (sourceType === 'Film/Movie') {
				return polishReferenceOutput(generateFilm(data));
			} else if (sourceType === 'YouTube Video') {
				return polishReferenceOutput(generateYouTubeVideo(data));
			} else if (sourceType === 'TV Series Episode') {
				return polishReferenceOutput(generateTVEpisode(data));
			} else if (sourceType === 'Podcast Episode') {
				return polishReferenceOutput(generatePodcastEpisode(data));
			} else if (
				sourceType === 'Webinar/Lecture (recorded & retrievable)' ||
				sourceType === 'Webinar/Lecture'
			) {
				return polishReferenceOutput(generateWebinar(data));
			} else if (sourceType === 'Song/Track') {
				return polishReferenceOutput(generateSongTrack(data));
			} else if (sourceType === 'Infographic/Chart/Graph') {
				return polishReferenceOutput(generateInfographic(data));
			} else if (sourceType === 'Radio Broadcast') {
				return polishReferenceOutput(generateRadioBroadcast(data));
			}
		}

		// Social Media
		if (category === 'Social Media') {
			if (sourceType === 'X/Twitter Post') {
				return polishReferenceOutput(generateTwitterPost(data));
			}
		}

		// Personal Communication
		if (category === 'Personal Communication') {
			return 'Personal communications are cited only in-text and do not appear in the reference list.';
		}

		// Government & Institutional Publications
		if (category === 'Government & Institutional Publications') {
			if (sourceType === 'Press Release') {
				return polishReferenceOutput(generatePressRelease(data));
			} else if (sourceType === 'Institutional Report') {
				return polishReferenceOutput(generateInstitutionalReport(data));
			}
		}

		// Data & Software
		if (category === 'Data & Software') {
			if (sourceType === 'Data set') {
				return polishReferenceOutput(generateDataset(data));
			}
		}

		// Course & Education
		if (category === 'Course & Education') {
			if (sourceType === 'Course Handout') {
				return polishReferenceOutput(generateCourseHandout(data));
			} else if (sourceType === 'Conference Presentation / Session') {
				const mode = resolveConferenceUnifiedSubcategory(data);
				if (mode === 'Conference Session') {
					return polishReferenceOutput(generateConferenceSession(data));
				}
				return polishReferenceOutput(generateConferencePresentation(data));
			} else if (sourceType === 'Conference Presentation') {
				return polishReferenceOutput(generateConferencePresentation(data));
			} else if (sourceType === 'Open Educational Resource') {
				return polishReferenceOutput(generateOER(data));
			} else if (sourceType === 'Conference Session') {
				return polishReferenceOutput(generateConferenceSession(data));
			}
		}

		return 'Fill in the fields to see your citation...';
	} catch (error) {
		return 'Fill in the required fields to generate citation...';
	}
};

// Generate in-text citation
// options.isFirstOrganizationCite — false after this source has been inserted in the document (APA group author subsequent form).
export const generateInText = (category, sourceType, data, options = {}) => {
	if (!category || !sourceType || !data) {
		return { parenthetical: '', narrative: '' };
	}
	const firstOrg = options.isFirstOrganizationCite !== false;

	// Personal Communication (special case)
	if (category === 'Personal Communication') {
		const author = formatPersonalCommunicationNameForInText(data.authorCommunicator || '');
		const date = data.date || 'Date';
		return {
			parenthetical: `(${author}, personal communication, ${date})`,
			narrative: `${author} (personal communication, ${date})`
		};
	}

	// Multimedia - Handle non-public items as personal communication
	if (category === 'Multimedia' && data.publiclyAccessible === 'No') {
		let author = 'Author';
		if (sourceType === 'Webinar/Lecture' || sourceType === 'Webinar/Lecture (recorded & retrievable)') {
			const pres = data.presentersLecturers || [];
			author =
				pres.length > 0
					? formatNameForInText(pres[0])
					: data.organization
						? getOrganizationCitationParts(data).fullName || data.organization
						: 'Author';
		} else {
			const authors =
				data.authorOrganization || data.authorUploader || data.artistGroup || data.contributors || [];
			author = authors.length > 0 ? formatNameForInText(authors[0]) : 'Author';
		}
		const date = data.date || 'Date';
		return {
			parenthetical: `(${author}, personal communication, ${date})`,
			narrative: `${author} (personal communication, ${date})`
		};
	}

	// Social Media - X/Twitter Post
	if (category === 'Social Media' && sourceType === 'X/Twitter Post') {
		if (data.publiclyAccessible === 'No') {
			const handle = data.handleUsername?.trim();
			const nameFromAuthor =
				data.author?.length > 0 && data.author[0]?.trim()
					? formatPersonalCommunicationNameForInText(data.author[0])
					: '';
			const nameFromField = data.authorName?.trim()
				? formatPersonalCommunicationNameForInText(data.authorName)
				: '';
			const author = nameFromAuthor || nameFromField || (handle ? `@${handle}` : 'Communicator');
			const date = data.date || 'Date';
			return {
				parenthetical: `(${author}, personal communication, ${date})`,
				narrative: `${author} (personal communication, ${date})`
			};
		}
		const year = (data.date || data.year) ? extractYear(data.date || data.year) : 'n.d.';
		if (data.AuthorOrOrganization === 'Organization' && data.organization) {
			return formatOrganizationIntextPair(data, year, firstOrg);
		}
		const authors =
			data.author?.some((a) => a && a.trim()) ? data.author : data.authorName ? [data.authorName] : [];
		const authorTextParenthetical = formatAuthorsForInText(authors, false);
		const authorTextNarrative = formatAuthorsForInText(authors, true);
		return {
			parenthetical: `(${authorTextParenthetical}, ${year})`,
			narrative: `${authorTextNarrative} (${year})`
		};
	}

	// Multimedia - YouTube Video (organization uploaders)
	if (category === 'Multimedia' && sourceType === 'YouTube Video') {
		const uploaders = (data.authorUploader || []).filter((a) => a && a.trim());
		const year = (data.date || data.year) ? extractYear(data.date || data.year) : 'n.d.';
		if (uploaders.length > 0 && looksLikeOrganizationName(uploaders[0])) {
			const org = uploaders[0].trim();
			return {
				parenthetical: `(${org}, ${year})`,
				narrative: `${org} (${year})`
			};
		}
		const authorTextParenthetical = formatAuthorsForInText(uploaders, false);
		const authorTextNarrative = formatAuthorsForInText(uploaders, true);
		return {
			parenthetical: `(${authorTextParenthetical}, ${year})`,
			narrative: `${authorTextNarrative} (${year})`
		};
	}

	// Web Content - Webpage
	if (category === 'Web Content' && sourceType === 'Webpage') {
		const year = (data.date || data.year) ? extractYear(data.date || data.year) : 'n.d.';
		if (data.AuthorOrOrganization === 'Organization' && data.organization) {
			return formatOrganizationIntextPair(data, year, firstOrg);
		}
		const authorTextParenthetical = formatAuthorsForInText(data.author || [], false);
		const authorTextNarrative = formatAuthorsForInText(data.author || [], true);
		return {
			parenthetical: `(${authorTextParenthetical}, ${year})`,
			narrative: `${authorTextNarrative} (${year})`
		};
	}

	// Web Content - Blog Post
	if (category === 'Web Content' && sourceType === 'Blog Post') {
		const year = (data.date || data.year) ? extractYear(data.date || data.year) : 'n.d.';
		if (data.AuthorOrOrganization === 'Organization' && data.organization) {
			return formatOrganizationIntextPair(data, year, firstOrg);
		}
		const authorTextParenthetical = formatAuthorsForInText(data.author || [], false);
		const authorTextNarrative = formatAuthorsForInText(data.author || [], true);
		return {
			parenthetical: `(${authorTextParenthetical}, ${year})`,
			narrative: `${authorTextNarrative} (${year})`
		};
	}

	// TV Series Episode — in-text uses writers only (APA); et al. for 3+
	if (category === 'Multimedia' && sourceType === 'TV Series Episode') {
		const writers = data.writers || [];
		const year = (data.date || data.year) ? extractYear(data.date || data.year) : 'n.d.';
		const authorTextParenthetical = formatAuthorsForInText(writers, false);
		const authorTextNarrative = formatAuthorsForInText(writers, true);
		return {
			parenthetical: `(${authorTextParenthetical}, ${year})`,
			narrative: `${authorTextNarrative} (${year})`
		};
	}

	// Podcast Episode — in-text uses host(s) only
	if (category === 'Multimedia' && sourceType === 'Podcast Episode') {
		const contributors = data.contributors || [];
		const roles = data.contributorRoles || [];
		const hosts = contributors.filter((c, i) => {
			const r = (roles[i] || '').toLowerCase();
			return r === 'host' || r.startsWith('host ');
		});
		const forIntext = hosts.length > 0 ? hosts : contributors;
		const year = (data.date || data.year) ? extractYear(data.date || data.year) : 'n.d.';
		const authorTextParenthetical = formatAuthorsForInText(forIntext, false);
		const authorTextNarrative = formatAuthorsForInText(forIntext, true);
		return {
			parenthetical: `(${authorTextParenthetical}, ${year})`,
			narrative: `${authorTextNarrative} (${year})`
		};
	}

	// Course & Education — Course Handout
	if (category === 'Course & Education' && sourceType === 'Course Handout') {
		const presenters = data.presenterOrganization || [];
		const year = (data.date || data.year) ? extractYear(data.date || data.year) : 'n.d.';
		const forIntext = formatMixedNameListForInText(presenters);
		const authorTextParenthetical = formatAuthorsForInText(forIntext, false);
		const authorTextNarrative = formatAuthorsForInText(forIntext, true);
		return {
			parenthetical: `(${authorTextParenthetical}, ${year})`,
			narrative: `${authorTextNarrative} (${year})`
		};
	}

	// Multimedia — Song/Track
	if (category === 'Multimedia' && sourceType === 'Song/Track') {
		const artists = data.artistGroup || [];
		const year = (data.date || data.year) ? extractYear(data.date || data.year) : 'n.d.';
		const forIntext = formatMixedNameListForInText(artists);
		const authorTextParenthetical = formatAuthorsForInText(forIntext, false);
		const authorTextNarrative = formatAuthorsForInText(forIntext, true);
		return {
			parenthetical: `(${authorTextParenthetical}, ${year})`,
			narrative: `${authorTextNarrative} (${year})`
		};
	}

	// Course & Education — Conference Presentation/Session
	if (
		category === 'Course & Education' &&
		(sourceType === 'Conference Presentation / Session' ||
			sourceType === 'Conference Presentation' ||
			sourceType === 'Conference Session')
	) {
		const contributors = data.contributorsOrganization || data.presentersOrganization || [];
		const roles = data.contributorRoles || [];
		const year = (data.date || data.year) ? extractYear(data.date || data.year) : 'n.d.';
		const forIntext = contributors
			.filter((c) => c && c.trim())
			.map((name, i) => {
				const role = roles[i] || '';
				if (role === 'Organization' || looksLikeOrganizationName(name, role)) {
					return name.trim();
				}
				return formatNameForInText(name);
			});
		const authorTextParenthetical = formatAuthorsForInText(forIntext, false);
		const authorTextNarrative = formatAuthorsForInText(forIntext, true);
		return {
			parenthetical: `(${authorTextParenthetical}, ${year})`,
			narrative: `${authorTextNarrative} (${year})`
		};
	}

	// Periodicals - Handle no author cases (Magazine, Newspaper, Preprint)
	if (category === 'Periodicals') {
		const year = (data.date || data.year) ? extractYear(data.date || data.year) : 'n.d.';

		// Check if author/organization is selected
		const hasAuthor =
			data.AuthorOrOrganization &&
			((data.AuthorOrOrganization === 'Author(s)' && data.author?.some((a) => a && a.trim())) ||
				(data.AuthorOrOrganization === 'Organization' && data.organization));

		// If no author, use shortened title
		if (!hasAuthor) {
			let title = '';
			if (sourceType === 'Magazine Article (Print & Online)') {
				title = data.titleOfArticle || 'Title';
			} else if (sourceType === 'Newspaper Article (Print & Online)') {
				title = data.titleOfArticle || 'Title';
			} else if (sourceType === 'Preprint') {
				title = data.titleOfManuscript || 'Title';
			}

			// Shorten title to first few words (max 4-5 words)
			const words = title.split(' ');
			const shortTitle = words.length > 4 ? words.slice(0, 4).join(' ') + '...' : title;

			// For articles use quotes, for preprint use italics
			const formattedTitle =
				sourceType === 'Preprint'
					? shortTitle // Will be italicized in the UI
					: `"${shortTitle}"`;

			return {
				parenthetical: `(${formattedTitle}, ${year})`,
				narrative: `${formattedTitle} (${year})`
			};
		}

		// If author exists, use standard format with new fields
		if (data.AuthorOrOrganization === 'Organization' && data.organization) {
			return formatOrganizationIntextPair(data, year, firstOrg);
		}
		const authorTextParenthetical = formatAuthorsForInText(data.author || [], false);
		const authorTextNarrative = formatAuthorsForInText(data.author || [], true);
		return {
			parenthetical: `(${authorTextParenthetical}, ${year})`,
			narrative: `${authorTextNarrative} (${year})`
		};
	}

	// Get author and year for standard in-text citations
	const year = (data.date || data.year) ? extractYear(data.date || data.year) : 'n.d.';
	const originalYear = data.originalWorkPublishedYear || '';
	const yearText = originalYear ? `${year}/${originalYear}` : year;

	if (data.AuthorOrOrganization === 'Organization' && data.organization) {
		return formatOrganizationIntextPair(data, yearText, firstOrg);
	}

	const authors = resolveInTextAuthorList(category, sourceType, data);
	const authorTextParenthetical = formatAuthorsForInText(authors, false);
	const authorTextNarrative = formatAuthorsForInText(authors, true);

	return {
		parenthetical: `(${authorTextParenthetical}, ${yearText})`,
		narrative: `${authorTextNarrative} (${yearText})`
	};
};

// Specific generators for each source type

// Helper to get author text based on Author/Organization selection
function getAuthorText(data, fallback = 'Author') {
	if (data.AuthorOrOrganization === 'Organization' && data.organization) {
		const { fullName } = getOrganizationCitationParts(data);
		return fullName || fallback;
	}
	return formatAuthorsForReference(data.author || []) || fallback;
}

function generateBlogPost(data) {
	const authors = getAuthorText(data);
	const dateRaw = data.date || data.year;
	const date = dateRaw ? formatAPAReferenceParentheticalDate(dateRaw) : 'n.d.';
	const title = data.titleOfPost ? toSentenceCase(data.titleOfPost) : 'Title of post';
	const blogName = data.blogName || 'Blog Name';
	const url = data.url || 'URL';

	return `${authors} (${date}). ${title}. <em>${blogName}</em>. ${url}`;
}

function generateWebpage(data) {
	const authors = getAuthorText(data);
	const dateRaw = data.date || data.year;
	const datePart = dateRaw ? formatAPAReferenceParentheticalDate(dateRaw) : 'n.d.';
	const title = data.titleOfWebPage ? toSentenceCase(data.titleOfWebPage) : 'Title of web page';
	const siteRaw = (data.websiteName || '').trim();
	const omitSite = shouldOmitPublisherWhenMatchesAuthor(data, authors, siteRaw);
	const siteName = !omitSite && siteRaw ? ` ${siteRaw.replace(/\.\s*$/, '')}.` : '';
	const url = data.url || 'URL';
	const retrievalPhrase = data.retrievalDate ? formatRetrievalDatePhrase(data.retrievalDate) : '';
	const retrieval = retrievalPhrase ? `Retrieved ${retrievalPhrase}, from ` : '';

	return `${authors} (${datePart}). <em>${title}</em>.${siteName} ${retrieval}${url}`;
}

function generateJournalArticle(data) {
	const authors = getAuthorText(data);
	const yearRaw = data.year || data.date;
	const yearPart = yearRaw ? formatAPAReferenceParentheticalDate(yearRaw) : 'n.d.';
	const title = data.titleOfArticle ? toSentenceCase(data.titleOfArticle) : 'Title of article';
	const journal = data.titleOfJournal || 'Journal Title';
	const volume = data.volume || 'Volume';
	const issue = data.issue ? `(${data.issue})` : '';

	let pageRange = data.pageRange;
	if (data.pageRangeStart || data.pageRangeEnd) {
		const start = data.pageRangeStart || '';
		const end = data.pageRangeEnd || '';
		if (start || end) {
			if (start === end) {
				pageRange = start;
			} else {
				pageRange = `${start}-${end}`;
			}
		}
	}
	const pages = pageRange ? formatPageRange(pageRange) : '';
	const pageSeg = pages ? `, ${pages}` : '';
	const doi = (data.doiUrl || '').trim();

	return `${authors} (${yearPart}). ${title}. <em>${journal}</em>, <em>${volume}</em>${issue}${pageSeg}. ${doi}`.replace(
		/,\s*,/g,
		', '
	);
}

function generateMagazineArticle(data) {
	const hasAuthor =
		data.AuthorOrOrganization &&
		((data.AuthorOrOrganization === 'Author(s)' && data.author?.some((a) => a && a.trim())) ||
			(data.AuthorOrOrganization === 'Organization' && data.organization));
	const authors = hasAuthor ? getAuthorText(data, '') : '';
	const dateRaw = data.date || '';
	const date = dateRaw ? formatAPAReferenceParentheticalDate(dateRaw) : 'n.d.';
	const title = data.titleOfArticle ? toSentenceCase(data.titleOfArticle) : 'Title of article';
	const magazine = data.titleOfMagazine || 'Magazine Title';

	let volIssuePart = '';
	if (data.volume && String(data.volume).trim()) {
		volIssuePart = `, <em>${data.volume.trim()}</em>`;
		if (data.issue && String(data.issue).trim()) {
			volIssuePart += `(${data.issue.trim()})`;
		}
	}

	let pageRange = data.pageRange;
	if (data.pageRangeStart || data.pageRangeEnd) {
		const start = data.pageRangeStart || '';
		const end = data.pageRangeEnd || '';
		if (start || end) {
			if (start === end) {
				pageRange = start;
			} else {
				pageRange = `${start}-${end}`;
			}
		}
	}
	const pages = pageRange ? `, ${formatPageRange(String(pageRange))}` : '';
	const isOnline = data.publishedOnline === 'Yes';
	const doi = isOnline ? (data.doiUrl || '').trim() : '';

	let ref = !authors
		? `${title}. (${date}). <em>${magazine}</em>`
		: `${authors} (${date}). ${title}. <em>${magazine}</em>`;
	if (volIssuePart) ref += volIssuePart;
	if (pages) ref += pages;
	ref += doi ? `. ${doi}` : '.';
	return ref.replace(/,\s*,/g, ', ').replace(/\.\s*\./g, '.').trim();
}

function generateNewspaperArticle(data) {
	const hasAuthor =
		data.AuthorOrOrganization &&
		((data.AuthorOrOrganization === 'Author(s)' && data.author?.some((a) => a && a.trim())) ||
			(data.AuthorOrOrganization === 'Organization' && data.organization));
	const authors = hasAuthor ? getAuthorText(data, '') : '';
	const dateRaw = data.date || '';
	const date = dateRaw ? formatAPAReferenceParentheticalDate(dateRaw) : 'n.d.';
	const title = data.titleOfArticle ? toSentenceCase(data.titleOfArticle) : 'Title of article';
	const newspaper = data.titleOfNewspaper || 'Newspaper Title';

	let pageRange = data.pageNumbers;

	if (data.pageNumbersStart || data.pageNumbersEnd) {
		const start = data.pageNumbersStart || '';
		const end = data.pageNumbersEnd || '';
		if (start || end) {
			if (start === end) {
				pageRange = start;
			} else {
				pageRange = `${start}-${end}`;
			}
		}
	} else if (!pageRange && (data.pageRangeStart || data.pageRangeEnd)) {
		const start = data.pageRangeStart || '';
		const end = data.pageRangeEnd || '';
		if (start || end) {
			if (start === end) {
				pageRange = start;
			} else {
				pageRange = `${start}-${end}`;
			}
		}
	}

	const pages = pageRange ? `, ${formatPageRange(String(pageRange))}` : '';
	const isOnline = data.publishedOnline === 'Yes';
	const url = isOnline ? (data.doiUrl || '').trim() : '';

	let ref = !authors
		? `${title}. (${date}). <em>${newspaper}</em>`
		: `${authors} (${date}). ${title}. <em>${newspaper}</em>`;
	if (pages) ref += pages;
	ref += url ? `. ${url}` : '.';
	return ref.replace(/,\s*,/g, ', ').replace(/\.\s*\./g, '.').trim();
}

function generatePreprint(data) {
	const authors = getAuthorText(data, '');
	const dateRaw = data.date || data.year;
	const yearPart = dateRaw ? formatAPAReferenceParentheticalDate(dateRaw) : 'n.d.';
	const title = data.titleOfManuscript ? toSentenceCase(data.titleOfManuscript) : 'Title of manuscript';
	const version = data.version ? ` (Version ${data.version})` : '';
	const descriptor = data.descriptor || 'Preprint';
	const repository = data.repositoryArchiveName || 'Repository Name';
	const doi = data.doiUrl || 'URL';

	// If no author, start with title
	if (!authors) {
		return `<em>${title}</em>${version}. (${yearPart}). [${descriptor}]. ${repository}. ${doi}`;
	}

	return `${authors} (${yearPart}). <em>${title}</em>${version} [${descriptor}]. ${repository}. ${doi}`;
}

function generateBook(data) {
	const authors = formatAuthorsForReference(data.authors || data.author || []);
	const translatorList = getTranslatorList(data);
	const translators =
		translatorList.length > 0 ? formatTranslatorsForReference(translatorList) : '';
	const year = data.year ? extractYear(data.year) : 'n.d.';
	const title = data.titleOfBook ? toSentenceCase(data.titleOfBook) : 'Title of book';
	const publisherRaw = (data.publisher || '').trim();
	const omitPub = shouldOmitPublisherWhenMatchesAuthor(data, authors, publisherRaw);
	const publisher =
		omitPub || !publisherRaw ? '' : ` ${formatPublisherName(publisherRaw)}.`;
	const doi = data.doiUrl || '';
	const originalYear = data.originalWorkPublishedYear || '';

	// Build the parenthetical information (translator, edition, volume)
	// APA: semicolon after Trans. when edition and/or volume follows
	const trailingParts = [];
	if (data.edition) {
		trailingParts.push(`${formatEdition(data.edition)} ed.`);
	}
	if (data.volumes) {
		trailingParts.push(formatVolumeLabel(data.volumes));
	}
	const parentheticalText = formatTranslatorEditionVolumeParenthetical({
		translatorText: translators,
		trailingParts,
	});

	// Original work published year note
	const originalYearText = originalYear ? ` (Original work published ${originalYear})` : '';

	// Compose reference and clean spacing before punctuation
	let reference = `${authors || 'Author'} (${year}). <em>${title}</em>${parentheticalText}.${publisher} ${doi}${originalYearText}`;

	// Remove any accidental spaces before punctuation and trim
	reference = reference.replace(/\s+([.,;:])/g, '$1').trim();

	return reference;
}

function generateBookChapter(data) {
	const authors = formatAuthorsForReference(data.chapterAuthors || []);
	const year = data.year ? extractYear(data.year) : 'n.d.';
	const chapterTitle = data.titleOfChapter ? toSentenceCase(data.titleOfChapter) : 'Title of chapter';
	const editors = formatEditorsForReference(data.editors || data.editor || []);
	const translatorList = getTranslatorList(data);
	const translators =
		translatorList.length > 0 ? formatTranslatorsForReference(translatorList) : '';
	const editorLabel = data.editors && data.editors.length > 1 ? 'Eds.' : 'Ed.';
	const bookTitle = data.titleOfBook ? toSentenceCase(data.titleOfBook) : 'Title of book';
	// const pages = data.pageRange || "pp. XX–XX"
	const publisherRaw = (data.publisher || '').trim();
	const publisher = publisherRaw ? formatPublisherName(publisherRaw) : 'Publisher';
	const doi = data.doiUrl || '';
	const originalYear = data.originalWorkPublishedYear || '';

	// Build the book parenthetical information (translator, edition, volume, page range)
	// APA: semicolon after Trans. when edition/volume/pages follow
	const bookTrailingParts = [];

	// Add edition
	if (data.edition) {
		bookTrailingParts.push(`${formatEdition(data.edition)} ed.`);
	}

	// Add volume
	if (data.volumes) {
		bookTrailingParts.push(formatVolumeLabel(data.volumes));
	}

	// Add page range
	let pageRange = data.pageRange;
	// Prioritize separate fields if they exist
	if (data.pageRangeStart || data.pageRangeEnd) {
		const start = data.pageRangeStart || '';
		const end = data.pageRangeEnd || '';
		if (start || end) {
			// If start equals end, display single page number
			if (start === end) {
				pageRange = start;
			} else {
				pageRange = `${start}-${end}`;
			}
		}
	}

	if (pageRange) {
		bookTrailingParts.push(`pp. ${formatPageRange(pageRange)}`);
	}

	const bookParentheticalText = formatTranslatorEditionVolumeParenthetical({
		translatorText: translators,
		trailingParts: bookTrailingParts,
	});

	// Only include original work note for translated chapters (per SOW)
	const originalYearText =
		originalYear && translators ? ` (Original work published ${originalYear})` : '';

	return `${authors || 'Author'} (${year}). ${chapterTitle}. In ${editors || 'Editor'
		} (${editorLabel}), <em>${bookTitle}</em>${bookParentheticalText}. ${publisher}. ${doi}${originalYearText}`;
}

function generateReferenceEntry(data) {
	// Author can be individual entryAuthors or an organization
	let authors = '';
	if (data.AuthorOrOrganization === 'Organization' && data.organization) {
		// Use organization name as-is for group author
		authors = data.organization.trim();
	} else {
		authors = formatAuthorsForReference(data.entryAuthors || []);
	}

	const year = data.year ? extractYear(data.year) : 'n.d.';
	const title = data.titleOfEntry ? toSentenceCase(data.titleOfEntry) : 'Title of entry';
	const editors = formatEditorsForReference(data.referenceWorkEditors || []);
	const translatorList = getTranslatorList(data);
	const translators =
		translatorList.length > 0 ? formatTranslatorsForReference(translatorList) : '';
	const refWork = data.titleOfReferenceWork ? toSentenceCase(data.titleOfReferenceWork) : 'Reference Work Title';
	const publisherRaw = (data.publisher || '').trim();
	const publisher = publisherRaw ? formatPublisherName(publisherRaw) : 'Publisher';
	const urlRaw = (data.doiUrl || '').trim();
	const isOnline = !!urlRaw;
	const retrieval = data.retrievalDate
		? `Retrieved ${formatRetrievalDatePhrase(data.retrievalDate)}, from `
		: '';
	const originalYear = data.originalWorkPublishedYear || '';
	const entryEditors = data.entryEditors ? formatTranslatorsForReference(data.entryEditors) : '';
	const entryEditorsText = entryEditors ? ` [Revised by ${entryEditors}]` : '';

	// Build the reference work parenthetical information (translator, edition, volume, page range)
	// APA: semicolon after Trans. when edition/volume/pages follow
	const refWorkTrailingParts = [];

	// Add edition
	if (data.edition) {
		refWorkTrailingParts.push(`${formatEdition(data.edition)} ed.`);
	}

	// Add volume
	if (data.volumes) {
		refWorkTrailingParts.push(formatVolumeLabel(data.volumes));
	}

	// Add page range
	let pageRange = data.pageRange;
	// Prioritize separate fields if they exist
	if (data.pageRangeStart || data.pageRangeEnd) {
		const start = data.pageRangeStart || '';
		const end = data.pageRangeEnd || '';
		if (start || end) {
			// If start equals end, display single page number
			if (start === end) {
				pageRange = start;
			} else {
				pageRange = `${start}-${end}`;
			}
		}
	}

	// Print entries use page range; online entries use URL (mutually exclusive per APA workflow)
	if (pageRange && !isOnline) {
		refWorkTrailingParts.push(`pp. ${formatPageRange(pageRange)}`);
	}

	const refWorkParentheticalText = formatTranslatorEditionVolumeParenthetical({
		translatorText: translators,
		trailingParts: refWorkTrailingParts,
	});

	const originalYearText = originalYear ? ` (Original work published ${originalYear})` : '';
	const urlSegment = isOnline ? `${retrieval}${urlRaw}` : '';

	if (editors) {
		const editorLabel =
			data.referenceWorkEditors && data.referenceWorkEditors.length > 1 ? 'Eds.' : 'Ed.';
		return `${authors || 'Author'
			} (${year}). ${title}${entryEditorsText}. In ${editors} (${editorLabel}), <em>${refWork}</em>${refWorkParentheticalText}. ${publisher}. ${urlSegment}${originalYearText}`;
	}

	return `${authors || 'Author'
		} (${year}). ${title}${entryEditorsText}. In <em>${refWork}</em>${refWorkParentheticalText}. ${publisher}. ${urlSegment}${originalYearText}`;
}

function generateThesis(data) {
	const authors = formatAuthorsForReference(data.authors || []);
	const year = data.year ? extractYear(data.year) : 'n.d.';
	const title = data.titleOfThesisDissertation ? toSentenceCase(data.titleOfThesisDissertation) : 'Title';
	const publicationNumber = data.publicationNumberAccessionId || '';
	const publicationNumberText = publicationNumber ? ` (Publication No. ${publicationNumber})` : '';
	const descriptor = data.descriptor || 'Doctoral dissertation';
	const institution = (data.institution || 'Institution').trim();
	const repository = (data.publisherRepository || 'Repository').trim();
	const doi = (data.doiUrl || '').trim();
	const publicationStatus = data.publicationStatus || '';

	const buildUnpublishedBracket = () => {
		const isMasters =
			/master/i.test(descriptor) ||
			(publicationStatus === 'Unpublished' && /master/i.test(descriptor));
		const label = isMasters ? "Unpublished master's thesis" : 'Unpublished doctoral dissertation';
		return `[${label}, ${institution}]`;
	};

	// Unpublished thesis/dissertation (via Publication status)
	if (publicationStatus === 'Unpublished') {
		const descriptorBracket = buildUnpublishedBracket();
		return `${authors || 'Author'} (${year}). <em>${title}</em>${publicationNumberText} ${descriptorBracket}. ${institution}.`;
	}

	// Normalize bracketed descriptor segment for published works
	const descriptorBracket = `[${descriptor}, ${institution}]`;

	// Online but not in a database – institution acts as publisher, URL/DOI optional
	if (publicationStatus === 'Online (not in database)') {
		return `${authors || 'Author'} (${year}). <em>${title}</em>${publicationNumberText} ${descriptorBracket}. ${institution}.${doi ? ` ${doi}` : ''}`;
	}

	// Database, print-only, or fallback: repository optional, URL/DOI optional
	const repoSegment = repository ? `${repository}.` : '';
	const doiSegment = doi ? ` ${doi}` : '';

	return `${authors || 'Author'} (${year}). <em>${title}</em>${publicationNumberText} ${descriptorBracket}. ${repoSegment}${doiSegment}`;
}

function generateFilm(data) {
	const contributorsList = data.contributors || [];
	const roles = data.contributorRoles || [];
	const dateRaw = data.date || data.year;
	const yearParen = dateRaw ? formatAPAReferenceParentheticalDate(dateRaw) : 'n.d.';
	const title = data.titleOfWork ? toSentenceCase(data.titleOfWork) : 'Title of film';
	const descriptor = data.descriptor || 'Film';
	const company = (data.productionCompany || '').trim().replace(/\.\s*$/, '');
	const platform = (data.platform || '').trim().replace(/\.\s*$/, '');
	const url = (data.url || '').trim();

	const contributorsText =
		formatContributorsWithRolesForReference(contributorsList, roles, { orgSeparator: '; ' }) ||
		'Director (Director)';

	let studioSeg = '';
	if (company && platform) {
		studioSeg = `${company}. ${platform}.`;
	} else if (company) {
		studioSeg = `${company}.`;
	} else if (platform) {
		studioSeg = `${platform}.`;
	}

	let out = `${contributorsText}. (${yearParen}). <em>${title}</em> [${descriptor}].`;
	if (studioSeg) out += ` ${studioSeg}`;
	if (url) out += ` ${url}`;
	return out.replace(/\s+/g, ' ').replace(/(\.\s*){2,}/g, '. ').trim();
}

/** TV reference: Writer(s)/Director(s) each with role; Oxford comma before & before last credit. */
function formatTVWritingAndDirectingCredits(writerNames, directorNames) {
	const w = (writerNames || []).filter((x) => x && x.trim()).map((n) => `${formatNameForReference(n)} (Writer)`);
	const d = (directorNames || []).filter((x) => x && x.trim()).map((n) => `${formatNameForReference(n)} (Director)`);
	const all = [...w, ...d];
	if (all.length === 0) return '';
	if (all.length === 1) return all[0];
	if (all.length === 2) return `${all[0]} & ${all[1]}`;
	return `${all.slice(0, -1).join(', ')}, & ${all[all.length - 1]}`;
}

/** Executive producers: F. M. LastName style; plural label when 2+. */
function formatExecutiveProducersForTVReference(names) {
	const list = (names || []).filter((x) => x && x.trim());
	if (list.length === 0) return '';
	const parts = list.map((n) => formatTranslatorForReference(n));
	if (parts.length === 1) return `${parts[0]} (Executive Producer)`;
	const nameJoined =
		parts.length === 2 ? `${parts[0]} & ${parts[1]}` : `${parts.slice(0, -1).join(', ')}, & ${parts[parts.length - 1]}`;
	return `${nameJoined} (Executive Producers)`;
}

function mergePodcastContributorsForReference(contributorsList, roles) {
	const merged = new Map();
	(contributorsList || []).forEach((c, i) => {
		if (!c || !String(c).trim()) return;
		const key = String(c).trim().toLowerCase();
		const role = (roles[i] || 'Host').trim();
		if (!merged.has(key)) merged.set(key, { raw: String(c).trim(), roles: [] });
		merged.get(key).roles.push(role);
	});
	return Array.from(merged.values()).map(({ raw, roles: rlist }) => {
		const uniq = [...new Set(rlist)];
		if (uniq.some((r) => r === 'Organization (no role assigned)')) {
			return raw;
		}
		const lower = uniq.map((x) => x.toLowerCase());
		let label = uniq[0];
		if (lower.includes('host') && lower.includes('producer')) label = 'Host and Producer';
		else if (lower.includes('host') && lower.includes('writer')) label = 'Host and Writer';
		else if (uniq.length === 1) label = uniq[0];
		else label = uniq.join(' & ');
		return `${formatNameForReference(raw)} (${label})`;
	});
}

function joinPodcastContributorSegments(segments) {
	if (segments.length === 0) return 'Host (Host)';
	if (segments.length === 1) return segments[0];
	if (segments.length === 2) return `${segments[0]}, & ${segments[1]}`;
	return `${segments.slice(0, -1).join(', ')}, & ${segments[segments.length - 1]}`;
}

function generateYouTubeVideo(data) {
	// Check if publicly accessible
	if (data.publiclyAccessible === 'No') {
		return 'Personal communications are cited only in-text and do not appear in the reference list.';
	}

	let authorPart = '';
	const uploaders = (data.authorUploader || []).filter((a) => a && a.trim());
	if (uploaders.length > 0 && looksLikeOrganizationName(uploaders[0])) {
		authorPart = uploaders[0].trim();
	} else if (data.organization?.trim()) {
		authorPart = getOrganizationCitationParts(data).fullName || data.organization.trim();
	} else {
		authorPart = formatAuthorsForReference(uploaders) || 'Author';
	}
	const rawHandle = (data.handleUsername || '').trim().replace(/^@+/, '');
	const username = rawHandle ? ` [@${rawHandle}]` : '';
	const dateRaw = data.date || data.year;
	const date = dateRaw ? formatAPAReferenceParentheticalDate(dateRaw) : 'n.d.';
	const titlePlain = data.titleOfVideo ? toSentenceCase(data.titleOfVideo) : 'Title of video';
	const title = `<em>${titlePlain}</em>`;
	const descriptor = data.descriptor || 'Video';
	const platform = (data.platformName || 'YouTube').trim().replace(/\.\s*$/, '');
	const url = (data.url || '').trim() || 'URL';

	return `${authorPart}${username} (${date}). ${title} [${descriptor}]. ${platform}. ${url}`;
}

function generateTVEpisode(data) {
	const credits = formatTVWritingAndDirectingCredits(data.writers, data.directors);
	const creditsSeg = credits || 'Writer (Writer), & Director (Director)';
	const epSeg = formatExecutiveProducersForTVReference(data.executiveProducers);
	const execProducerText = epSeg ? `${epSeg}, ` : '';
	const dateRaw = data.date || data.year;
	const date = dateRaw ? formatAPAReferenceParentheticalDate(dateRaw) : 'n.d.';
	const title = data.titleOfEpisode ? toSentenceCase(data.titleOfEpisode) : 'Title of episode';
	const season = data.season ? `Season ${data.season}` : '';
	const episode = data.episodeNumber ? `Episode ${data.episodeNumber}` : '';
	const seasonEpisode =
		season && episode ? ` (${season}, ${episode})` : season ? ` (${season})` : episode ? ` (${episode})` : '';
	const descriptor = data.descriptor || 'TV series episode';
	const series = data.titleOfSeries ? toSentenceCase(data.titleOfSeries) : 'Series Title';
	const company = (data.productionCompany || 'Production Company').trim().replace(/\.\s*$/, '');
	const url = (data.url || '').trim();

	return `${creditsSeg}. (${date}). ${title}${seasonEpisode} [${descriptor}]. In ${execProducerText}<em>${series}</em>. ${company}.${url ? ` ${url}` : ''}`;
}

function generatePodcastEpisode(data) {
	const contributorsList = data.contributors || [];
	const roles = data.contributorRoles || [];
	const dateRaw = data.date || data.year;
	const date = dateRaw ? formatAPAReferenceParentheticalDate(dateRaw) : 'n.d.';
	const title = data.titleOfEpisode ? toSentenceCase(data.titleOfEpisode) : 'Title of episode';
	const episodeNum = data.episodeNumber ? ` (No. ${data.episodeNumber})` : '';
	const descriptor = data.descriptor || 'Audio podcast episode';
	const podcast = data.titleOfPodcast ? toSentenceCase(data.titleOfPodcast) : 'Podcast Title';
	const pubRaw = (data.publisherNetwork || '').trim();
	const publisher = pubRaw ? ` ${formatPublisherName(pubRaw)}.` : '';
	const url = (data.url || '').trim() || 'URL';

	const segments = mergePodcastContributorsForReference(contributorsList, roles);
	const contributorsText = joinPodcastContributorSegments(segments);

	return `${contributorsText}. (${date}). ${title}${episodeNum} [${descriptor}]. In <em>${podcast}</em>.${publisher ? `${publisher}` : ''} ${url}`.replace(
		/\s+/g,
		' '
	).trim();
}

function generateWebinar(data) {
	// Check if publicly accessible
	if (data.publiclyAccessible === 'No') {
		return 'Personal communications are cited only in-text and do not appear in the reference list.';
	}

	const presentersList = data.presentersLecturers || [];
	const roles = data.presenterRoles || [];
	const dateRaw = data.date || data.year;
	const date = dateRaw ? formatAPAReferenceParentheticalDate(dateRaw) : 'n.d.';
	const titlePlain = data.titleOfRecording || data.title ? toSentenceCase(data.titleOfRecording || data.title) : 'Title';
	const title = `<em>${titlePlain}</em>`;
	const descriptor = data.descriptor || 'Webinar';
	const eventInstitution = (data.eventInstitution || '').trim().replace(/\.\s*$/, '');
	const eventSeg = eventInstitution ? `${eventInstitution}. ` : '';
	const platform = (data.platform || 'Platform').trim().replace(/\.\s*$/, '');
	const url = ((data.doiUrl || data.url) || '').trim() || 'URL';

	const validPresenters = presentersList.filter((p) => p && p.trim());
	const hasRoles = roles && roles.length > 0 && roles.some((r) => r && r.trim());

	let presentersText = '';
	if (validPresenters.length === 0) {
		presentersText = 'Presenter';
	} else if (hasRoles) {
		presentersText = formatContributorsWithRolesForReference(validPresenters, roles);
	} else {
		presentersText = formatAuthorsForReference(validPresenters);
	}

	return `${presentersText}. (${date}). ${title} [${descriptor}]. ${eventSeg}${platform}. ${url}`;
}

function generateSongTrack(data) {
	// Check if publicly accessible
	if (data.publiclyAccessible === 'No') {
		return 'Personal communications are cited only in-text and do not appear in the reference list.';
	}

	const artists = formatMixedNameListForReference(data.artistGroup || []);
	const year = (data.date || data.year) ? extractYear(data.date || data.year) : 'n.d.';
	const title = data.titleOfSong ? toSentenceCase(data.titleOfSong) : 'Title of song';
	const descriptor = data.descriptor || 'Song';
	const album = data.titleOfAlbum ? ` On <em>${toSentenceCase(data.titleOfAlbum)}</em>.` : '';
	const label = data.recordLabel ? ` ${formatPublisherName(data.recordLabel)}.` : '';
	const platform = data.platform ? ` ${formatPublisherName(data.platform)}.` : '';
	const url = data.url ? ` ${data.url}` : '';

	return `${artists || 'Artist'} (${year}). ${title} [${descriptor}].${album}${label}${platform}${url}`;
}

function generateInfographic(data) {
	const authors = getAuthorText(data);
	const dateRaw = data.date || data.year;
	const date = dateRaw ? formatAPAReferenceParentheticalDate(dateRaw) : 'n.d.';
	const title = data.titleOfWork ? toSentenceCase(data.titleOfWork) : 'Title of work';
	const descriptor = data.descriptor || 'Descriptor';
	const publisher = data.platformPublisher ? ` ${formatPublisherName(data.platformPublisher)}.` : '';
	const retrieval = data.retrievalDate
		? `Retrieved ${formatRetrievalDatePhrase(data.retrievalDate)}, from `
		: '';
	const url = data.doiUrl || 'URL';

	return `${authors} (${date}). <em>${title}</em> [${descriptor}].${publisher} ${retrieval}${url}`;
}

function generateRadioBroadcast(data) {
	const contributorsList = data.contributorsOrganization || [];
	const roles = data.contributorRoles || [];
	const dateRaw = data.date || data.year;
	const date = dateRaw ? formatAPAReferenceParentheticalDate(dateRaw) : 'Year, Month Day';
	const title = data.titleOfEpisode ? toSentenceCase(data.titleOfEpisode) : 'Title of episode';
	const episodeNum = data.episodeNumber ? ` (No. ${data.episodeNumber})` : '';
	const descriptor = data.descriptor || 'Radio broadcast';
	const series = data.titleOfSeries || 'Radio Series';
	const company = data.productionCompany || 'Production Company';
	const url = data.url ? ` ${data.url}` : '';

	const contributorsText =
		formatContributorsWithRolesForReference(contributorsList, roles) || 'Host (Host)';

	return `${contributorsText}. (${date}). <em>${title}</em>${episodeNum} [${descriptor}]. In <em>${series}</em>. ${company}.${url}`;
}

function generateTwitterPost(data) {
	// Check if publicly accessible
	if (data.publiclyAccessible === 'No') {
		return 'Personal communications are cited only in-text and do not appear in the reference list.';
	}

	// Handle Author(s) vs Organization
	let authorText = '';
	if (data.AuthorOrOrganization === 'Organization' && data.organization) {
		authorText = data.organization;
	} else {
		authorText = formatAuthorsForReference(data.author || []) || '';
		if (!authorText && data.authorName?.trim()) {
			authorText = formatAuthorsForReference([data.authorName.trim()]);
		}
		if (!authorText && data.handleUsername?.trim()) {
			authorText = `[@${data.handleUsername.trim()}]`;
		}
		if (!authorText) {
			authorText = 'Author';
		}
	}

	const handle = data.handleUsername?.trim();
	let nameAndHandle = authorText;
	if (handle) {
		if (nameAndHandle.includes(`[@${handle}]`)) {
			if (!nameAndHandle.endsWith('.')) {
				nameAndHandle = `${nameAndHandle}.`;
			}
		} else if (nameAndHandle) {
			nameAndHandle = `${nameAndHandle} [@${handle}].`;
		} else {
			nameAndHandle = `[@${handle}].`;
		}
	}

	const yearPart =
		data.date || data.year ? formatAPAReferenceParentheticalDate(data.date || data.year) : 'n.d.';
	const rawTitle = truncateToWordCount(
		data.titleOfPost ? toSentenceCase(data.titleOfPost) : 'Title of post',
		20
	);
	const title = `<em>${rawTitle}</em>`;
	const descriptor = data.descriptor || 'Post';
	const platform = (data.platform || 'X').trim().replace(/\.\s*$/, '');
	const url = (data.url || '').trim() || 'URL';
	const retrievalPhrase = data.retrievalDate ? formatRetrievalDatePhrase(data.retrievalDate) : '';
	const retrieval = retrievalPhrase ? `Retrieved ${retrievalPhrase}, from ` : '';

	return `${nameAndHandle} (${yearPart}). ${title} [${descriptor}]. ${platform}. ${retrieval}${url}`;
}

function generatePressRelease(data) {
	const authors = getAuthorText(data, 'Organization');
	const yearPart = data.date ? extractYear(data.date) : 'n.d.';
	const title = data.titleOfPressRelease ? toSentenceCase(data.titleOfPressRelease) : 'Title of press release';
	const descriptor = data.descriptor || 'Press release';
	const url = (data.url || '').trim() || 'URL';

	// APA Rule: If author and publisher are the same, omit publisher
	const publisherValue = (data.websitePublisher || '').trim().toLowerCase();
	let isRedundantPublisher = false;

	if (publisherValue) {
		if (data.AuthorOrOrganization === 'Organization' && data.organization) {
			const { fullName } = parseOrganizationName(data.organization);
			const cleanOrgName = fullName.trim().toLowerCase();
			isRedundantPublisher =
				publisherValue === cleanOrgName || publisherValue === authors.trim().toLowerCase();
		} else {
			const authorAuthors = formatAuthorsForReference(data.author || []);
			const compareTo = authorAuthors || authors;
			isRedundantPublisher = publisherValue === compareTo.trim().toLowerCase();
		}
	}

	const pubTrim = (data.websitePublisher || '').trim();
	let out = `${authors} (${yearPart}). <em>${title}</em> [${descriptor}].`;
	if (pubTrim && !isRedundantPublisher) {
		out += ` ${formatPublisherName(pubTrim)}.`;
	}
	if (url) {
		out += ` ${url}`;
	}
	return out.replace(/\s+/g, ' ').trim();
}

function generateInstitutionalReport(data) {
	const authors = getAuthorText(data, 'Organization');
	const year = data.year ? extractYear(data.year) : 'n.d.';
	const title = data.titleOfReport ? toSentenceCase(data.titleOfReport) : 'Title of report';
	const reportNum = data.reportNumber ? ` (Report No. ${data.reportNumber})` : '';

	// APA Rule: If author and publisher are the same, omit publisher
	const publisherValue = (data.publisher || '').trim().toLowerCase();
	let isRedundantPublisher = false;

	if (publisherValue) {
		if (data.AuthorOrOrganization === 'Organization' && data.organization) {
			const { fullName } = getOrganizationCitationParts(data);
			const cleanOrgName = fullName.trim().toLowerCase();
			// Check against full name OR the formatted authors string (which might include abbreviation)
			isRedundantPublisher =
				publisherValue === cleanOrgName || publisherValue === authors.trim().toLowerCase();
		} else {
			isRedundantPublisher = publisherValue === authors.trim().toLowerCase();
		}
	}

	const publisher = data.publisher && !isRedundantPublisher ? ` ${formatPublisherName(data.publisher)}.` : '';
	const url = data.doiUrl || '';

	return `${authors} (${year}). <em>${title}</em>${reportNum}.${publisher} ${url}`;
}

function generateDataset(data) {
	const authors = getAuthorText(data);
	const year = data.year ? extractYear(data.year) : 'n.d.';
	const titlePlain = data.titleOfDataset ? toSentenceCase(data.titleOfDataset) : 'Title of dataset';
	const title = `<em>${titlePlain}</em>`;
	const version = formatDatasetVersionForReference(data.version);
	const descriptor = data.descriptor || 'Data set';
	const publisher = (data.publisher || '').trim();
	const url = (data.doiUrl || '').trim();
	const retrievalPhrase = data.retrievalDate ? formatRetrievalDatePhrase(data.retrievalDate) : '';
	const retrievalText = retrievalPhrase ? `Retrieved ${retrievalPhrase}, from ` : '';

	let out = `${authors} (${year}). ${title}${version} [${descriptor}].`;
	if (publisher) {
		out += ` ${formatPublisherName(publisher)}.`;
	}
	if (retrievalText && url) {
		out += ` ${retrievalText}${url}`;
	} else if (url) {
		out += ` ${url}`;
	} else if (retrievalText) {
		out += ` ${retrievalText.trim().replace(/,\s*from\s*$/, '')}`;
	}
	return out.replace(/\s+/g, ' ').trim();
}

function generateCourseHandout(data) {
	const presenter = formatMixedNameListForReference(data.presenterOrganization || []);
	const dateRaw = data.date || data.year;
	const date = dateRaw ? formatAPAReferenceParentheticalDate(dateRaw) : 'Year';
	const title = data.titleOfPresentation ? toSentenceCase(data.titleOfPresentation) : 'Title';
	const format = data.format || 'Course handout';
	const dept = data.department ? `${data.department}, ` : '';
	const institution = data.institution || 'Institution';
	const url = data.url || '';
	const course = data.course ? `${data.course}, ` : '';

	return `${presenter || 'Instructor'} (${date}). ${title} [${format}]. ${course}${dept}${institution}. ${url}`
		.replace(/\s+/g, ' ')
		.trim();
}

function formatConferenceContributorsForReference(contributorsList, roles = []) {
	const validContributors = (contributorsList || []).filter((c) => c && c.trim());
	if (validContributors.length === 0) return '';

	const formattedNames = validContributors.map((contributor, index) => {
		const role = (roles[index] || '').trim();
		if (role === 'Organization' || looksLikeOrganizationName(contributor, role)) {
			return contributor.trim();
		}
		const name = formatNameForReference(contributor);
		if (!role) return name;
		return `${name} (${role})`;
	});

	if (formattedNames.length === 1) return formattedNames[0];
	if (formattedNames.length === 2) return `${formattedNames[0]}, & ${formattedNames[1]}`;
	const allButLast = formattedNames.slice(0, -1).join(', ');
	return `${allButLast}, & ${formattedNames[formattedNames.length - 1]}`;
}

function generateConferencePresentation(data) {
	const contributorsList = data.contributorsOrganization || data.presentersOrganization || [];
	const roles = data.contributorRoles || [];
	const presenters =
		formatConferenceContributorsForReference(contributorsList, roles) ||
		formatAuthorsForReference(contributorsList);
	const dateRaw = data.date || data.year;
	const date = dateRaw ? formatAPAReferenceParentheticalDate(dateRaw) : 'Year, Month';
	const titleField = data.titleOfPresentation || data.titleOfPaper;
	const title = titleField ? toSentenceCase(titleField) : 'Title of presentation';
	const descriptor = data.descriptor || 'Conference presentation';
	const conferenceName = data.nameOfConference || data.conferenceDetails || 'Conference Name';
	const location = (data.locationOfConference || '').trim();
	const conferenceSeg = location ? `${conferenceName}, ${location}` : conferenceName;
	const url = (data.doiUrl || data.url || '').trim();
	const urlSeg = data.publiclyAccessible === 'Yes' && url ? ` ${url}` : '';

	return `${presenters}. (${date}). ${title} [${descriptor}]. ${conferenceSeg}.${urlSeg}`
		.replace(/\.\s*\./g, '.')
		.replace(/\s+/g, ' ')
		.trim();
}

function buildOerEditionVersionParenthetical(data) {
	const parts = [];
	if (data.edition) {
		parts.push(`${formatEdition(data.edition)} ed.`);
	}
	if (data.version) {
		const v = String(data.version)
			.trim()
			.replace(/^version\s*/i, '');
		if (v) parts.push(`Version ${v}`);
	}
	// Backward compatibility for legacy combined field
	if (!parts.length && data.versionEdition) {
		const legacy = String(data.versionEdition).trim();
		if (/edition|ed\.?$/i.test(legacy)) {
			parts.push(`${formatEdition(legacy)} ed.`);
		} else {
			const v = legacy.replace(/^version\s*/i, '');
			if (v) parts.push(`Version ${v}`);
		}
	}
	return parts.length ? ` (${parts.join(', ')})` : '';
}

function generateOER(data) {
	const authors = getAuthorText(data);
	const year = data.year ? extractYear(data.year) : 'n.d.';
	const title = data.titleOfOer ? toSentenceCase(data.titleOfOer) : 'Title of OER';
	const version = buildOerEditionVersionParenthetical(data);
	const descRaw =
		data.descriptor === 'Other' && (data.descriptorCustom || '').trim()
			? (data.descriptorCustom || '').trim()
			: data.descriptor || '';
	const descriptor = descRaw ? ` [${descRaw}]` : '';
	const publisherRaw = (data.platformPublisher || '').trim();
	const url = (data.url || '').trim();
	const omitPub =
		url ||
		(publisherRaw ? shouldOmitPublisherWhenMatchesAuthor(data, authors, publisherRaw) : false);
	const publisher = !omitPub && publisherRaw ? formatPublisherName(publisherRaw) : '';
	const retrievalPhrase = data.retrievalDate ? formatRetrievalDatePhrase(data.retrievalDate) : '';
	const retrievalText = retrievalPhrase ? `Retrieved ${retrievalPhrase}, from ` : '';

	const pubSeg = publisher ? `. ${publisher}` : '';
	return `${authors || 'Author'} (${year}). <em>${title}</em>${version}${descriptor}${pubSeg}. ${retrievalText}${url || 'URL'}`;
}

function generateConferenceSession(data) {
	const contributorsList = data.contributorsOrganization || [];
	const roles = data.contributorRoles || [];
	const dateRaw = data.date || data.year;
	const date = dateRaw ? formatAPAReferenceParentheticalDate(dateRaw) : 'Year, Month Day';
	const title = data.titleOfPresentation ? toSentenceCase(data.titleOfPresentation) : 'Title of session';
	const descriptor = data.descriptor || 'Conference session';
	const conference = data.nameOfConference || 'Conference Name';
	const location = data.locationOfConference || 'Location';
	const url = (data.doiUrl || data.url || '').trim();
	const urlText = url ? ` ${url}` : '';

	// Check if publicly accessible
	if (data.publiclyAccessible === 'No') {
		return 'Personal communications are cited only in-text and do not appear in the reference list.';
	}

	// Format contributors with their roles
	let contributorsText = formatConferenceContributorsForReference(contributorsList, roles);
	if (!contributorsText) {
		contributorsText = 'Chair (Chair)';
	}

	return `${contributorsText}. (${date}). ${title} [${descriptor}]. ${conference}, ${location}.${urlText}`;
}
